import { memo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { HStack } from "@design-system/HStack/HStack";
import { getVisibleOrgBillingTransferRequest } from "../../clients/helpers/getVisibleOrgBillingTransferRequest";
import { OrgBillingTransferBadge } from "../../clients/views/cells/OrgBillingTransferBadge";
import { ClientsCloseOverviewRow } from "../clientsCloseOverviewViewConfig";
import styles from "./CloseExtraColumns.module.scss";

export const CloseBillingOwnershipCell = memo(
    ({
        row,
    }: CellContext<
        ViewTableFeatures,
        ClientsCloseOverviewRow,
        boolean | undefined
    >) => {
        const organization = useActiveOrganization();

        if (row.original.rowType !== "workspace") {
            return null;
        }

        if (row.original.isOrganizationBillingOwner === undefined) {
            return <span aria-label="Loading who pays">-</span>;
        }

        if (!row.original.isOrganizationBillingOwner) {
            return <span className={styles.billingOwnerName}>Client</span>;
        }

        if (!organization) {
            return null;
        }

        const transferRequest = getVisibleOrgBillingTransferRequest({
            organization,
            client: {
                orgBillingTransferRequest:
                    row.original.orgBillingTransferRequest ?? null,
            },
        });

        return (
            <HStack gap={8} align="center">
                <Tooltip content={organization.name}>
                    <span className={styles.billingOwnerName}>Firm</span>
                </Tooltip>
                {transferRequest && (
                    <OrgBillingTransferBadge request={transferRequest} />
                )}
            </HStack>
        );
    },
);

CloseBillingOwnershipCell.displayName = "CloseBillingOwnershipCell";
